/**
 * Changelog data for the Traveller Ship Builder application
 */

export interface ChangelogEntry {
  version: string;
  date: string;
  title: string;
  changes: {
    category: 'feature' | 'bugfix' | 'improvement' | 'breaking';
    description: string;
  }[];
}

export const CHANGELOG: ChangelogEntry[] = [
  {
    version: '0.1.5',
    date: '2025-10-20',
    title: 'Crew Auto-Assignment & Stateroom Sync Fixes',
    changes: [
      {
        category: 'bugfix',
        description: 'Fixed errors preventing auto-assignment of staterooms from working correctly. Staterooms now update automatically based on assigned crew with coverage percentage respected.',
      },
      {
        category: 'improvement',
        description: 'Introduced proportional auto-assignment for crew: assigned values increase/decrease by the change in recommended counts without overwriting user-added headroom.',
      },
      {
        category: 'bugfix',
        description: 'Stewards and Medics are now properly accounted for in recommendations and assignments (Stewards: 1 per 10 High or 100 Middle, rounded up; Medics: 1 per 120 crew+passengers, rounded up).',
      },
      {
        category: 'bugfix',
        description: 'Fixed Safari load error in Ship Builder by hardening formatting utilities (null/undefined guards) and removing unstable effect dependencies to prevent maximum update depth loops.',
      },
      {
        category: 'improvement',
        description: 'Living Spaces UX: replaced “Coverage” checkbox with “Manually Override Stateroom Allocations” toggle; auto-assign coverage moved left and remains disabled while manual override is enabled.',
      },
      {
        category: 'improvement',
        description: 'Accommodation Summary and Step 11 auto-assign now use the assigned crew total consistently to size staterooms.',
      },
    ],
  },
  {
    version: '0.1.4',
    date: '2025-01-16',
    title: 'Stateroom Auto-Assignment System',
    changes: [
      {
        category: 'feature',
        description: 'Automatic stateroom and living spaces allocation - automatically assigns accommodations for crew and passengers with configurable coverage percentage and manual override option',
      },
      {
        category: 'bugfix',
        description: 'Fixed drive calculation discrepancies between drive summary sections and footer - now uses centralized calculation functions for consistent values',
      },
      {
        category: 'improvement',
        description: 'Removed "Available Space" field from drive and hull summary footers for cleaner UI',
      },
      {
        category: 'bugfix',
        description: 'Fixed input field validation issues - ship size, armor protection, and armor type selection now work correctly with proper validation and user control',
      },
      {
        category: 'bugfix',
        description: 'Fixed fuel tank and power plant auto-calculations - resolved multiple issues with fuel allocation, power calculations, and real-time updates when drives are added or modified',
      },
      {
        category: 'bugfix',
        description: 'Fixed app initialization and user progression issues - resolved crashes and UX problems that prevented users from starting ship design',
      },
      {
        category: 'bugfix',
        description: 'Fixed sensor and power plant cost calculations - resolved multiple calculation discrepancies including sensor cost scaling, power plant MCr conversion, and middleware execution order to ensure accurate header totals',
      },
      {
        category: 'improvement',
        description: 'Enhanced display precision - improved cost and tonnage formatting to show 2 decimal places throughout the application for more accurate information display',
      },
      {
        category: 'bugfix',
        description: 'Fixed computer system calculations - corrected computer option costs in ship totals and Jump Control Specialisation (/bis) bandwidth reduction logic to properly apply only to the active primary computer',
      },
      {
        category: 'improvement',
        description: 'Added contextual UI guidance - included helpful warning notes in Computer Selector to explain when backup computer options won\'t affect software calculations',
      },
      {
        category: 'bugfix',
        description: 'Fixed Safari browser compatibility - added null/undefined checks to all formatting functions to prevent crashes when displaying numeric values',
      },
    ],
  },
  {
    version: '0.1.3',
    date: '2025-01-08',
    title: 'Small Craft Integration, UI Improvements & Critical Bug Fixes',
    changes: [
      {
        category: 'feature',
        description: 'Integrated small craft assignment into Optional Systems step - assign craft directly when adding docking systems',
      },
      {
        category: 'feature',
        description: 'Moved Small Craft Assignment tab from Finalise Design to Step 12 (Cargo Configuration) and renamed to "Adjust Small Craft Assignments"',
      },
      {
        category: 'feature',
        description: 'Automatic power plant and fuel tank updates when living spaces (staterooms, low berths, etc.) are changed',
      },
      {
        category: 'feature',
        description: 'Automatic power plant and fuel tank updates when optional systems with power requirements are added or removed',
      },
      {
        category: 'feature',
        description: 'Moved Supplies and Ammunition tabs from Finalise Design to Step 12 and separated into distinct tabs',
      },
      {
        category: 'bugfix',
        description: 'Fixed critical armor cost calculation bug - now uses armor tonnage instead of ship size with correct size multipliers',
      },
      {
        category: 'bugfix',
        description: 'Fixed double-counting of internal small craft weights in total ship tonnage calculations',
      },
      {
        category: 'bugfix',
        description: 'Fixed docking clamp weight integration in drive performance calculations for external small craft',
      },
      {
        category: 'bugfix',
        description: 'Fixed automatic fuel tank updates for Jump Drives - now properly includes Jump Drive Reserve in calculations',
      },
      {
        category: 'bugfix',
        description: 'Fixed "Weeks of Power Plant Operation" spinner rounding issue - now restricts input to whole numbers only',
      },
      {
        category: 'bugfix',
        description: 'Fixed Power Plant size display - now rounds to nearest 0.01 tons for better readability',
      },
      {
        category: 'bugfix',
        description: 'Fixed Crystaliron armor cost (Cr200,000 per ton) and Bonded Superdense armor cost (Cr500,000 per ton)',
      },
      {
        category: 'bugfix',
        description: 'Fixed armor cost calculations to properly account for hull type modifiers (streamlined hulls pay 20% more)',
      },
      {
        category: 'bugfix',
        description: 'Implemented hull size validation rules - minimum 5 tons for all ships, minimum 100 tons for jump-capable ships',
      },
      {
        category: 'bugfix',
        description: 'Fixed ESLint warnings in ExternalSystems, InternalSystems, and SmallCraftAssignmentModal components',
      },
      {
        category: 'improvement',
        description: 'Moved Crew Requirements box to top of Accommodation Summary Column for better workflow',
      },
      {
        category: 'improvement',
        description: 'Updated hull size validation messages - removed upper limit display per Traveller rules (no upper size limit)',
      },
      {
        category: 'improvement',
        description: 'Added visual validation warnings for invalid hull sizes with clear Traveller rules citations',
      },
      {
        category: 'improvement',
        description: 'Updated footer comment from "Bridge Power" to "Power Required" for consistency',
      },
      {
        category: 'improvement',
        description: 'Updated stateroom labels to "Stateroom (single occupancy)" and "Stateroom (double occupancy)" for clarity',
      },
      {
        category: 'improvement',
        description: 'Changed "occupant" to "occupancy" in stateroom labels for grammatical consistency',
      },
      {
        category: 'improvement',
        description: 'Updated all "Single Staterooms" and "Double Staterooms" labels to "Stateroom (single)" and "Stateroom (double)" in Accommodation Summary',
      },
      {
        category: 'improvement',
        description: 'Added power plant type warnings - Fission and Chemical power plants cannot support Jump Drive power requirements',
      },
      {
        category: 'improvement',
        description: 'Enhanced small craft assignment workflow - users can assign craft immediately when adding docking systems or skip to assign later',
      },
      {
        category: 'improvement',
        description: 'Improved cargo configuration workflow - all cargo-related functionality consolidated in Step 12 with dedicated tabs',
      },
      {
        category: 'improvement',
        description: 'Streamlined Finalise Design step - now focused on warnings and summary only for cleaner interface',
      },
      {
        category: 'improvement',
        description: 'Enhanced power management system - automatic updates for weapons, sensors, staterooms, and optional systems',
      },
      {
        category: 'improvement',
        description: 'Improved armor cost accuracy - calculations now match official Traveller specifications exactly',
      },
    ],
  },
  {
    version: '0.1.2',
    date: '2025-01-08',
    title: 'Weapon System Redesign & Automatic Power Management',
    changes: [
      {
        category: 'feature',
        description: 'Unified Weapons & Defensive Systems page - combined weapons and defensive systems into single interface',
      },
      {
        category: 'feature',
        description: 'Point Defence Battery and Screen mount types - proper Traveller rules implementation for defensive systems',
      },
      {
        category: 'feature',
        description: 'Automatic power plant and fuel tank updates when weapons/defensive systems are added or modified',
      },
      {
        category: 'feature',
        description: 'Automatic power plant and fuel tank updates when sensors are selected or modified',
      },
      {
        category: 'feature',
        description: 'Mount quantity system - single mount cards can represent multiple identical mounts with automatic calculations',
      },
      {
        category: 'feature',
        description: 'Dynamic turret types - automatically upgrade from single to double to triple based on weapons installed',
      },
      {
        category: 'feature',
        description: 'Firmpoint system for ships under 100 tons with range limitations and power reductions',
      },
      {
        category: 'feature',
        description: 'Fixed mount support for all ship sizes with up to 3 weapons per mount',
      },
      {
        category: 'bugfix',
        description: 'Fixed weapon tonnage calculations - weapons no longer consume volume except Railgun ammo',
      },
      {
        category: 'bugfix',
        description: 'Fixed mount tonnage calculations - mounts have fixed tonnage values with weapon tonnage added',
      },
      {
        category: 'bugfix',
        description: 'Fixed Railgun barbette tonnage - 1 canister (2 tons) contains 12 attacks worth of ammo',
      },
      {
        category: 'bugfix',
        description: 'Fixed Missile/Torpedo barbette tonnage on Firmpoints - added 2 tons for barbette mounting',
      },
      {
        category: 'bugfix',
        description: 'Fixed Spinal Mount ammo tonnage calculations for Mass Driver and Railgun weapons',
      },
      {
        category: 'bugfix',
        description: 'Removed invalid "Breakaway Hull Sensors" type - only official Traveller sensor types available',
      },
      {
        category: 'bugfix',
        description: 'Fixed compilation errors after removing invalid sensor types from breakaway hull configuration',
      },
      {
        category: 'bugfix',
        description: 'Fixed Fixed Mount dropdown visibility - now available for all ships, not just small craft',
      },
      {
        category: 'bugfix',
        description: 'Fixed all input boxes for dark mode compatibility across weapon assignment components',
      },
      {
        category: 'bugfix',
        description: 'Fixed System Preview box dark mode styling - container, labels, and text elements',
      },
      {
        category: 'improvement',
        description: 'Redesigned weapon assignment workflow - auto-add mounts on selection, configure in summary',
      },
      {
        category: 'improvement',
        description: 'Removed redundant weapon quantity selector to avoid user confusion',
      },
      {
        category: 'improvement',
        description: 'Removed redundant "Defensive Systems" and "Summary" tabs - unified interface',
      },
      {
        category: 'improvement',
        description: 'Updated labels to "System Assignment" and "Choose type to add..." for unified interface',
      },
      {
        category: 'improvement',
        description: 'Added Min TL display to all sensor dropdown entries for better user awareness',
      },
      {
        category: 'improvement',
        description: 'Enhanced mount quantity management - instant duplication with hardpoint/firmpoint validation',
      },
      {
        category: 'improvement',
        description: 'Improved ammunition system clarity - Fixed/Turret/Barbette use per-canister, Bays/Spinals use per-attack',
      },
      {
        category: 'improvement',
        description: 'Added comprehensive Point Defence weapons (Laser and Gauss batteries) with intercept scores',
      },
      {
        category: 'improvement',
        description: 'Added Screen systems (Meson Screen, Nuclear Damper, Black Globe Generator) with proper effects',
      },
      {
        category: 'improvement',
        description: 'Enhanced breakaway hull sensor configuration to use valid Traveller sensor types',
      },
    ],
  },
  {
    version: '0.1.1',
    date: '2025-01-08',
    title: 'Major Drive System Overhaul & Bug Fixes',
    changes: [
      {
        category: 'feature',
        description: 'Complete dark mode implementation across all Module Builder components',
      },
      {
        category: 'feature',
        description: 'Updates notification system with changelog modal and version tracking',
      },
      {
        category: 'feature',
        description: 'Multiple drive system - support for primary and additional drives of same type',
      },
      {
        category: 'feature',
        description: 'Custom ship size input with increment/decrement buttons and rounding logic',
      },
      {
        category: 'feature',
        description: 'Auto-selection of Standard Bridge when ship size is defined',
      },
      {
        category: 'feature',
        description: 'Auto-selection of best armor and power plant based on ship tech level',
      },
      {
        category: 'bugfix',
        description: 'Fixed Jump Drive fuel calculation - was 10x too small in some components',
      },
      {
        category: 'bugfix',
        description: 'Fixed Power Plant fuel calculation inconsistencies across breakaway sections',
      },
      {
        category: 'bugfix',
        description: 'Fixed critical bug where Power Plant fuel used power rating instead of tonnage',
      },
      {
        category: 'bugfix',
        description: 'Fixed jump fuel being required even when no jump engines are installed',
      },
      {
        category: 'bugfix',
        description: 'Fixed UI responsiveness issue where fuel tonnage only updated on blur',
      },
      {
        category: 'bugfix',
        description: 'Fixed additional drives incorrectly contributing to power requirements',
      },
      {
        category: 'bugfix',
        description: 'Fixed footer totals not updating when drives are selected/removed',
      },
      {
        category: 'bugfix',
        description: 'Fixed duplicate drive creation when selecting primary drives',
      },
      {
        category: 'bugfix',
        description: 'Fixed white text on white background in dark mode for input fields',
      },
      {
        category: 'bugfix',
        description: 'Fixed armor auto-selection preventing manual override to "None"',
      },
      {
        category: 'bugfix',
        description: 'Fixed tech level validation not enforcing minimum TL for armor types',
      },
      {
        category: 'bugfix',
        description: 'Fixed total cost showing Cr0 instead of correct value for new builds',
      },
      {
        category: 'bugfix',
        description: 'Fixed step navigation allowing access without valid ship size and tech level',
      },
      {
        category: 'bugfix',
        description: 'Fixed power plant auto-selection not working for higher tech levels',
      },
      {
        category: 'bugfix',
        description: 'Fixed duplicate tech level display in power plant dropdown options',
      },
      {
        category: 'improvement',
        description: 'Standardized all fuel calculations to use centralized functions',
      },
      {
        category: 'improvement',
        description: 'Added tech level filtering to Power Plant and Drive selectors',
      },
      {
        category: 'improvement',
        description: 'Simplified drive dropdowns to show only rating and Min TL',
      },
      {
        category: 'improvement',
        description: 'Removed "G" unit from drive ratings and updated Min TLs to match Traveller rules',
      },
      {
        category: 'improvement',
        description: 'Added "Thrust" prefix to M-Drive and R-Drive labels',
      },
      {
        category: 'improvement',
        description: 'Removed "Backup" drive role - simplified to Primary/Additional only',
      },
      {
        category: 'improvement',
        description: 'Added explanatory notes for Primary vs Additional drive behavior',
      },
      {
        category: 'improvement',
        description: 'Module Fuel Step now correctly uses installed power plants for fuel calculations',
      },
      {
        category: 'improvement',
        description: 'Enhanced step validation to require ship size and tech level before navigation',
      },
      {
        category: 'improvement',
        description: 'Improved power plant auto-selection to allow manual overrides',
      },
    ],
  },
  {
    version: '0.1.0-beta',
    date: '2025-10-01',
    title: 'Initial Beta Release',
    changes: [
      {
        category: 'feature',
        description: 'Complete ship builder interface with step-by-step configuration',
      },
      {
        category: 'feature',
        description: 'Module builder for creating custom ship modules',
      },
      {
        category: 'feature',
        description: 'Breakaway hull support for multi-section ships',
      },
      {
        category: 'feature',
        description: 'Comprehensive component library (drives, weapons, sensors, etc.)',
      },
      {
        category: 'feature',
        description: 'Real-time calculations for tonnage, power, and cost',
      },
      {
        category: 'feature',
        description: 'Export functionality for ship designs',
      },
      {
        category: 'feature',
        description: 'Dark mode support (partial)',
      },
    ],
  },
];

/**
 * Get the current version from the changelog
 */
export const getCurrentVersion = (): string => {
  return CHANGELOG[0]?.version || '0.1.4';
};

/**
 * Get the last seen version from localStorage
 */
export const getLastSeenVersion = (): string | null => {
  return localStorage.getItem('lastSeenVersion');
};

/**
 * Set the last seen version in localStorage
 */
export const setLastSeenVersion = (version: string): void => {
  localStorage.setItem('lastSeenVersion', version);
};

/**
 * Check if there are new updates
 */
export const hasNewUpdates = (): boolean => {
  const currentVersion = getCurrentVersion();
  const lastSeenVersion = getLastSeenVersion();
  
  if (!lastSeenVersion) {
    return true; // First time user, show updates
  }
  
  return currentVersion !== lastSeenVersion;
};

